import { useMemo, useState } from 'react';
import { useToast } from '@/components/common/toast';
import { FilterBar } from '@/components/common/FilterBar';
import { Select2 } from '@/components/forms/Select2';
import { AsyncButton } from '@/components/common/AsyncButton';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { ApiError } from '@/api/client/abpError';
import { marginControlApi } from '@/api/services/marginControl';
import {
  GroupMarginRateConfigurationStatus,
  type FetchMarginRateConfigurationsInput,
  type MarginControlOptionsDto,
  type MarginRateConfigurationListItemDto,
} from '@/api/types/marginControl';
import { fmtDateTime, fmtRate } from '../helpers';

interface Props {
  options: MarginControlOptionsDto;
  applyEnabled: boolean;
  applyDisabledReason: string;
  canApply: boolean;
  canUndo: boolean;
  onCrossReload: () => void;
}

export function ConfigurationsTab({ options, applyEnabled, applyDisabledReason, canApply, canUndo, onCrossReload }: Props) {
  const toast = useToast();
  const [nameDraft, setNameDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState('');
  const [applied, setApplied] = useState<{ name: string; status: string }>({ name: '', status: '' });
  const [backupTarget, setBackupTarget] = useState<MarginRateConfigurationListItemDto | null>(null);
  const [undoTarget, setUndoTarget] = useState<MarginRateConfigurationListItemDto | null>(null);

  const extra = useMemo<FetchMarginRateConfigurationsInput>(
    () => ({ nameFilter: applied.name || undefined, status: applied.status === '' ? undefined : Number(applied.status), sorting: 'appliedAt desc' }),
    [applied],
  );
  const table = useServerDataTable<MarginRateConfigurationListItemDto, FetchMarginRateConfigurationsInput>({
    queryKey: ['margin-control', 'configurations'],
    fetcher: (req) => marginControlApi.getConfigurations(req),
    pageSize: 5,
    extraParams: extra,
  });

  const fetch = () => { table.setPageIndex(0); setApplied({ name: nameDraft, status: statusDraft }); };

  const isBackup = (r: MarginRateConfigurationListItemDto) =>
    r.canApplyBackup || r.status === GroupMarginRateConfigurationStatus.Backup || r.statusLabel === 'Backup';

  const doApplyBackup = async () => {
    if (!backupTarget) return;
    try {
      const res = await marginControlApi.applyBackup({ configurationId: backupTarget.id });
      const ok = res.items.filter((i) => i.success).length;
      const fail = res.items.length - ok;
      const msg = `Backup applied: ${ok} succeeded, ${fail} failed (${res.configurationName}).`;
      if (fail > 0) toast.error(msg); else toast.success(msg);
      setBackupTarget(null);
      void table.refetch();
      onCrossReload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to apply backup.');
    }
  };

  const doUndo = async () => {
    if (!undoTarget) return;
    try {
      const res = await marginControlApi.undo(undoTarget.id);
      const msg = `Undo finished: restored ${res.restoredCount}, skipped ${res.skippedCount}, failed ${res.failedCount} (${res.statusLabel}).`;
      if (res.skippedCount > 0 || res.failedCount > 0) toast.error(msg); else toast.success(msg);
      setUndoTarget(null);
      void table.refetch();
      onCrossReload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to undo.');
    }
  };

  const columns: DataGridColumn<MarginRateConfigurationListItemDto>[] = [
    { key: 'name', title: 'Configuration Name', className: 'text-start', value: (r) => r.name },
    { key: 'modeLabel', title: 'Mode', className: 'text-start', value: (r) => r.modeLabel },
    { key: 'applyTypeLabel', title: 'Type', className: 'text-start', value: (r) => r.applyTypeLabel },
    { key: 'rate', title: 'Value', className: 'text-end', value: (r) => r.rate, cell: (r) => (isBackup(r) ? '—' : fmtRate(r.rate)) },
    { key: 'statusLabel', title: 'Status', className: 'text-start', value: (r) => r.statusLabel },
    { key: 'successCount', title: 'OK', className: 'text-end', value: (r) => r.successCount },
    { key: 'failureCount', title: 'Failed', className: 'text-end', value: (r) => r.failureCount },
    { key: 'appliedBy', title: 'Applied by', className: 'text-start', value: (r) => r.appliedBy },
    { key: 'appliedAt', title: 'Applied at', className: 'text-start', value: (r) => r.appliedAt, cell: (r) => fmtDateTime(r.appliedAt) },
  ];

  return (
    <div>
      {!applyEnabled && (
        <div className="alert alert-warning py-2" role="alert">
          {applyDisabledReason || 'Group Margin Rate Control is disabled in Tenant Configuration.'}
        </div>
      )}

      <FilterBar
        card={false}
        className="mb-3"
        fields={[
          <div key="name">
            <label className="form-label">Configuration Name</label>
            <input className="form-control" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetch()} />
          </div>,
          <Select2 key="status" label="Status" allowClear searchable={false} placeholder="All" value={statusDraft} options={options.statuses.map((s) => ({ value: s.value, label: s.text }))} onChange={(v) => setStatusDraft(v ?? '')} />,
        ]}
        actions={<AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" loading={table.isFetching} onClick={fetch}>Fetch</AsyncButton>}
      />

      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => r.id}
        card
        includeExcel
        excelFileName="Margin Rate Configurations"
        emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
        rowActions={(row) => {
          if (isBackup(row)) {
            return canApply ? (
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setBackupTarget(row)}>Apply backup</button>
            ) : null;
          }
          return row.canUndo && canUndo ? (
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setUndoTarget(row)}>Undo</button>
          ) : null;
        }}
        server={{ pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
      />

      <ConfirmDialog
        show={!!backupTarget}
        title="Apply backup?"
        message="Writes captured rates back to MT5. Paths that were Default stay Default. A new Applied row is created so you can Undo this restore."
        confirmLabel="Apply backup"
        confirmVariant="primary"
        onConfirm={doApplyBackup}
        onCancel={() => setBackupTarget(null)}
      />
      <ConfirmDialog
        show={!!undoTarget}
        title="Undo margin rates?"
        message="Restores previous group-security rates for rows that still match the applied values. Stale rows are skipped."
        confirmLabel="Undo"
        onConfirm={doUndo}
        onCancel={() => setUndoTarget(null)}
      />
    </div>
  );
}
